/**
 * Authentication provider for the LWP CRM frontend.
 *
 * Provides auth state (user, loading, error) and actions (login, logout) to the
 * entire React component tree. The context object, types and hooks (useAuth /
 * usePermissions) live in ./auth so this module exports ONLY the provider
 * component (keeps Fast Refresh + lint happy).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  login as apiLogin,
  logout as apiLogout,
  getMe,
  hasUsableAccessToken,
  refreshSession,
  clearTokens,
  completePendingLogout,
  onSessionExpired,
} from "../api/client";
import { clearStoredChats } from "../lib/support/chatStorage";
import { isLogoutPending } from "../lib/logoutPending";
import { stopViewAs } from "../api/viewAs";
import { clearViewAs } from "../lib/viewAs";
import type { User, LoginResponse } from "../api/client";
import { AuthContext } from "./auth";
import { ViewAsContext } from "./viewAs";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // On mount, restore the session (issue #972).
  //
  // A new tab / cold reload / bookmark has no per-tab access token, but the
  // HttpOnly refresh cookie persists across all of them. So: if there is no
  // usable (unexpired) access token, FIRST try to silently mint one from the
  // cookie; only then decide. `loading` stays true throughout so ProtectedRoute
  // shows its spinner and does NOT prematurely redirect to /login.
  useEffect(() => {
    let cancelled = false;
    async function restoreSession() {
      try {
        // D2-SEC-014 (#4081): a sign-out that never reached the server leaves
        // the refresh cookie live. Finish it first, and never restore from a
        // cookie that should already be dead.
        if (isLogoutPending() && !(await completePendingLogout())) return;
        // If the cached access token is missing/expired, refresh from the cookie
        // before giving up. refreshSession() returns null when there is no valid
        // refresh cookie (genuinely logged out).
        if (!hasUsableAccessToken()) {
          const access = await refreshSession();
          if (!access) {
            // No session to restore -- proceed as logged out (no token cleared
            // event needed; there was nothing to clear).
            return;
          }
        }
        const me = await getMe();
        if (!cancelled) setUser(me);
      } catch {
        // Token genuinely invalid -- clear and proceed as logged out.
        clearTokens();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for session-expired events dispatched by the API client when a 401
  // cannot be recovered (refresh token expired or absent). Setting user to null
  // makes ProtectedRoute redirect to /login on the next render.
  useEffect(() => {
    return onSessionExpired(() => {
      clearTokens();
      clearViewAs();
      clearStoredChats();
      setUser(null);
    });
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResponse> => {
      setError(null);
      setLoading(true);
      try {
        const response = await apiLogin(username, password);
        setUser(response.user);
        return response;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Login failed. Please try again.";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      // D2-SEC-011 (#3944): signing out ends any view-as session (audited).
      await stopViewAs();
      await apiLogout();
    } finally {
      // The help assistant's per-tab conversation goes with the session (#3624).
      clearStoredChats();
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me);
    } catch {
      // Leave the current user in place on a transient failure.
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
      refreshUser,
      clearError,
      isAuthenticated: !!user,
    }),
    [user, loading, error, login, logout, refreshUser, clearError],
  );

  // D2-SEC-011 (#3962): the single "viewing as an agent" flag, straight from
  // the server's `view_as` block -- read via `useIsViewingAs()`.
  const viewingAs = Boolean(user?.view_as);

  return (
    <AuthContext.Provider value={value}>
      <ViewAsContext.Provider value={viewingAs}>{children}</ViewAsContext.Provider>
    </AuthContext.Provider>
  );
}

// Backward-compatible re-export of useAuth. The hook now lives in ./auth (so
// this module can export ONLY the provider component), but one consumer
// (crm/documents/DocumentLibrary.tsx) is owned by a parallel work-stream (#609)
// and must not be edited from this branch to avoid a merge conflict. Re-export
// it here so that import keeps resolving. This single re-export is the only
// non-component export in this file; the lint rule is disabled for just this
// line with that justification.
// eslint-disable-next-line react-refresh/only-export-components
export { useAuth } from "./auth";
